import { useDispatch, useSelector } from 'react-redux'
import {
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { type UserData } from 'client/reducers/userReducer'
import { type StoreState } from 'client/store'
import getQueryParam, { queryParams } from 'common_packages/assets/js/utilities/getQueryParam'
import * as timerActions from 'common_packages/assets/js/actions/timerActions'
import * as uiActions from 'common_packages/assets/js/actions/uiActions'
import { type ApiResult, klubbleAPI } from 'client/api/common'
import { type DrawerType, drawerTypes } from 'common_packages/assets/js/types/drawerTypes'
import { isMobile } from 'common_packages/assets/js/utilities/devices'
import { formatTimeFromSeconds } from 'common_packages/assets/js/utilities/timer/formatTimeFromSeconds'
import TranslationContext from 'client/contexts/TranslationContext'
import translationGroups from 'client/constants/translationGroups'
import { type FinishedPuzzle } from 'client/types/FinishedPuzzle'
import { toastTypes } from 'common_packages/assets/js/types/toastTypes'
import { MINI } from 'client/constants/puzzleFormat'

type ShareLinkResponse = ApiResult<{ shareLink: string }>

const useShareDialog = () => {
  const dispatch = useDispatch()
  const { getRawTranslation } = useContext(TranslationContext)
  const { userName } = useSelector<StoreState, UserData>((state) => state.user?.data) || {}
  const klubblePuzzleId = getQueryParam(queryParams.KLUBBLE_PUZZLE_ID)

  const {
    nameLangKey, date: dateString, format, time, scoringBadges, shareTemplateTranslationKey, tries, visual, canGift,
  } = useSelector<StoreState, FinishedPuzzle>((state) => state.puzzleData?.finishedPuzzle) || {}

  const puzzleName = useMemo(() => {
    return `${getRawTranslation(translationGroups.puzzle, nameLangKey)}${format === MINI ? ` ${getRawTranslation(translationGroups.puzzleTags, 'mini')}` : ''}`
  }, [format, getRawTranslation, nameLangKey])

  const dateFormatted = useMemo(() => {
    const date = new Date(dateString)
    return `${date.getDate()}/${date.getMonth() + 1}/${date?.getFullYear()}`
  }, [dateString])

  const shareResultText = useMemo(() => {
    const points = String(scoringBadges?.find((b) => b.scoreType === 'total').score)

    const shareText = getRawTranslation(translationGroups.shareResult, shareTemplateTranslationKey, {
      puzzle: puzzleName,
      date: dateFormatted,
      time: formatTimeFromSeconds(time),
      points,
      tries,
      visual,
    })

    return shareText
  }, [scoringBadges, getRawTranslation, shareTemplateTranslationKey, puzzleName, dateFormatted, time, tries, visual])

  const openShareDialog = useCallback(async (shareType: 'gift' | 'share-result' | 'mp-invite', title, text = '') => {
    try {
      const payload = JSON.stringify({
        shareType,
        userNameInvitee: userName,
        klubblePuzzleId,
      })
      const { data: response } = await klubbleAPI.post<ShareLinkResponse>('puzzle/getsharelink', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      let { shareLink } = response.data

      if (process.env.KLUBBLE_REPLACE_SHARE_LINK === 'true') {
        // when running locally, use current location (ie: 'localhost')

        const regex = /^https:\/\/((app|jeux|play)\.)?(test|feature|uat)\.(klubble\.com|sportcerebral\.com|denksport\.com|tankesport\.se|klbbl\.com)/
        shareLink = shareLink
          .replace(regex, window.location.origin)
      }

      if (!canGift && shareType !== 'mp-invite') {
        shareLink = null
      }

      // Not all browsers support the `share` api
      if (navigator.share && isMobile) {
        navigator.share({
          title,
          text: text.replace('{link}', shareLink),
        }).catch(() => {
          // eslint-disable-next-line no-console
          console.warn('User cancelled share API.')
        }).finally(() => {
          if (shareType === 'mp-invite') {
            dispatch(timerActions.resumeTimer())
          }
        })
      } else {
        // fallback
        let fallbackDrawer: DrawerType

        if (shareType === 'gift') {
          fallbackDrawer = drawerTypes.GIFT_PUZZLE
        } else if (shareType === 'share-result') {
          fallbackDrawer = drawerTypes.SHARE_RESULT
        } else if (shareType === 'mp-invite') {
          fallbackDrawer = drawerTypes.INVITE_PUZZLE
        }

        dispatch(uiActions.setDrawer({ drawer: fallbackDrawer, drawerData: shareLink }))
      }
    } catch {
      dispatch(uiActions.addToast({
        message: getRawTranslation(translationGroups.sharePuzzle, 'something-went-wrong'),
        type: toastTypes.ERROR,
      }))
    }
  }, [userName, klubblePuzzleId, canGift, dispatch, getRawTranslation])

  return { openShareDialog, shareResultText }
}

export default useShareDialog
